import * as Yup from "yup";
import AccountCircleIcon from "@material-ui/icons/AccountCircle";
import Box from "@material-ui/core/Box";
import CircularProgress from "@material-ui/core/CircularProgress";
import MUIButton from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";
import React, { ChangeEvent, FC, useState } from "react";
import { Auth } from "aws-amplify";
import { Field, Form, Formik, FormikHelpers } from "formik";
import { Theme } from "@material-ui/core/styles";
import { styled, useTheme } from "@material-ui/styles";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";

import AppLink from "../../../../components/elements/AppLink";
import FormFieldError from "../../../../components/elements/FormFieldError";
import PasswordField from "../../../../components/redesign/elements/PasswordField";
import SocialAuthButtons from "../../../../components/modules/SocialAuthButtons";
import { CognitoAuthChallenge, resolveAuthChallenge } from "../../../../utils/cognitoAuthChallenge";
import { ReduxApplicationState } from "../../../../types/ReduxTypes/ReduxApplicationState";
import { getAppHomePath } from "../../../../utils/getAppHomePath";
import { getOauthRedirectError } from "../../../../utils/socialAuth";
import { setIsAuthLoading } from "../../../../redux/reducers/session";
import { ROUTE_PATHS } from "../../../../types/RoutePaths";

const RedesignedTextField = styled(TextField)(({ theme }: { theme: Theme }) => ({
  "& .MuiOutlinedInput-root": {
    "& fieldset": {
      border: `1px solid ${theme.palette.brand.gray200}`,
      borderRadius: 8,
    },
    "&.Mui-focused fieldset": {
      border: `1px solid ${theme.palette.brand.blueAccent}`,
    },
  },
}));

const CoralButton = styled(MUIButton)(({ theme }: { theme: Theme }) => ({
  backgroundColor: theme.palette.brand.coral,
  color: theme.palette.brand.white,
  borderRadius: 8,
  fontSize: 16,
  fontWeight: 600,
  height: 48,
  textTransform: "none",
  "&:hover": {
    backgroundColor: theme.palette.brand.coralHover,
  },
  "&:disabled": {
    backgroundColor: theme.palette.brand.coral,
    opacity: 0.7,
  },
}));

type RedesignedSignInFormProps = {
  onChallenge: (user: any, challenge: CognitoAuthChallenge) => void;
};

type FormInitialValues = {
  userName: string;
  password: string;
};

const validationSchema = Yup.object({
  userName: Yup.string().required("Required field."),
  password: Yup.string().required("Required field."),
});

const RedesignedSignInForm: FC<RedesignedSignInFormProps> = ({ onChallenge }) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const theme = useTheme<Theme>();
  const [authErrorMessage, setAuthErrorMessage] = useState<string>(getOauthRedirectError());
  const isAuthLoading = useSelector((state: ReduxApplicationState) => state.session.isLoading);

  const formInitialValues: FormInitialValues = { userName: "", password: "" };

  const handleSubmit = async (values: FormInitialValues, actions: FormikHelpers<FormInitialValues>) => {
    try {
      const { userName, password } = values;
      dispatch(setIsAuthLoading(true));
      const user = await Auth.signIn(userName, password);
      const challenge = await resolveAuthChallenge(user);

      if (challenge.type !== "SUCCESS") {
        onChallenge(user, challenge.type);
        return;
      }

      dispatch(setIsAuthLoading(false));
      actions.resetForm();
      history.push(getAppHomePath(), { fromLogin: true });
    } catch (error) {
      const err = error as any;
      dispatch(setIsAuthLoading(false));
      setAuthErrorMessage(`There was an error logging in: ${err.message}`);
    }
  };

  const handleFieldChange =
    (formikHandler: (e: string | ChangeEvent<any>) => void) => (e: string | ChangeEvent<any>) => {
      formikHandler(e);
      if (authErrorMessage) {
        setAuthErrorMessage("");
      }
    };

  return (
    <Formik
      initialValues={formInitialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      validateOnBlur={false}
    >
      {({ handleChange, values }) => (
        <Form>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
            <Typography style={{ fontSize: 24, fontWeight: 700, color: theme.palette.brand.gray900 }}>
              Log in
            </Typography>
            <AccountCircleIcon style={{ fontSize: 40, color: theme.palette.brand.gray900 }} />
          </Box>

          <Box>
            <Field
              as={RedesignedTextField}
              value={values.userName}
              error={!!authErrorMessage}
              name="userName"
              variant="outlined"
              label="Username"
              fullWidth
              onChange={handleFieldChange(handleChange("userName"))}
            />
            <Box style={{ minHeight: 30 }}>
              <FormFieldError name="userName" />
            </Box>
          </Box>

          <Box>
            <PasswordField
              value={values.password}
              error={!!authErrorMessage}
              name="password"
              label="Password"
              onChange={handleFieldChange(handleChange("password"))}
            />
            <Box style={{ minHeight: 30 }}>
              <FormFieldError name="password" />
              {authErrorMessage && (
                <Typography style={{ fontSize: 13, color: theme.palette.brand.errorRed }}>
                  {authErrorMessage}
                </Typography>
              )}
            </Box>
          </Box>

          <Box display="flex" justifyContent="flex-end" mb={3}>
            <AppLink to="/request-password-reset">Forgot Password?</AppLink>
          </Box>

          <CoralButton variant="contained" type="submit" fullWidth disabled={isAuthLoading} disableElevation>
            {isAuthLoading ? <CircularProgress size={22} style={{ color: theme.palette.brand.white }} /> : "Log in"}
          </CoralButton>

          <SocialAuthButtons onError={setAuthErrorMessage} />

          <Box display="flex" justifyContent="center" mt={2}>
            <span>
              <AppLink to={ROUTE_PATHS.SIGNUP}>Don't have an account?</AppLink>{" "}
              <AppLink to={ROUTE_PATHS.SIGNUP} highlighted>
                Register
              </AppLink>
            </span>
          </Box>
        </Form>
      )}
    </Formik>
  );
};

export default RedesignedSignInForm;
